// MARK: - SFX Catalog + Smart Default Selection
// Ported verbatim from Services/Audio/SoundEffectLibrary.swift

import type { SoundEffectCategory, SoundEffectOpportunity } from './types';
import { pick, pickIndex } from './rng';
import { SEMANTIC_MAP } from './semanticMap';

// ---------------------------------------------------------------------------
// CATALOG — private static let library in Swift (~lines 28-60)
// ---------------------------------------------------------------------------

export const CATALOG: Record<SoundEffectCategory, string[]> = {
  reactions: [
    // Original effects
    'record_scratch', 'rimshot', 'applause', 'gasp', 'bruh', 'crickets', 'dj_airhorn', 'dundundunnn',
    // New additions (12 effects)
    'aha_moment', 'awkward_silence', 'gasp_new', 'gulp', 'nooo', 'solo_applause',
    'correct_answer', 'party_horn', 'whoop_whoop', 'jackpot', 'tada_new', 'boxing_bell',
  ],
  objects: [
    // Original effects
    'car_crash', 'chomp', 'splash', 'thud', 'bell',
    // New additions (17 effects)
    'bark_dog', 'bone_crack', 'bonk', 'gun_cock', 'coin_collect', 'donk', 'munch',
    'oof', 'punch_hit', 'quack_duck', 'smooch', 'whip_smack', 'meow_cat',
    'ding_bell', 'kaching', 'pop_sound', 'game_jump',
  ],
  comedy: [
    // Original effects
    'slide_whistle', 'sad_trombone', 'duck_quack', 'fart',
    // New additions (14 effects)
    'anime_wow', 'bababoey', 'rehehe', 'rizz_sound', 'yeet', 'akh', 'huh',
    'do_it', 'mongolian_throat', 'stank', 'ba_dum_tss', 'cartoon_honk',
    'clown_horn', 'mcbeep',
  ],
  transitions: [
    // Original effects
    'whoosh', 'pop', 'ding', 'rizz', 'windows_xp',
    // New additions (15 effects)
    'alert', 'gong', 'siren_burst', 'tick_tock', 'undertaker_bell',
    'core_whoosh', 'dream_descend', 'dramatic_strings', 'heavenly_dream',
    'power_up', 'psycho_strings', 'sching', 'swish', 'twinkle', 'pluck',
  ],
};

// ---------------------------------------------------------------------------
// CATEGORY_FAVORITES — categoryFavorites dict in Swift (~lines 717-752)
// ---------------------------------------------------------------------------

export const CATEGORY_FAVORITES: Record<SoundEffectCategory, string[]> = {
  reactions: [
    'rimshot', 'applause', 'gasp_new', 'bruh', 'crickets',
    'aha_moment', 'nooo', 'boxing_bell', 'whoop_whoop', 'tada_new', 'jackpot',
  ],
  objects: [
    'thud', 'bonk', 'punch_hit', 'oof', 'pop_sound',
    'kaching', 'bark_dog', 'meow_cat', 'coin_collect', 'whip_smack',
  ],
  comedy: [
    'ba_dum_tss', 'slide_whistle', 'sad_trombone', 'duck_quack', 'yeet',
    'rizz_sound', 'huh', 'cartoon_honk', 'anime_wow', 'clown_horn',
  ],
  transitions: [
    'whoosh', 'swish', 'ding', 'power_up', 'alert',
    'dramatic_strings', 'twinkle', 'gong', 'sching',
  ],
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Return available effects for a category (mirrors getEffects(for:)). */
export function getEffects(category: SoundEffectCategory): string[] {
  return CATALOG[category];
}

/**
 * Select a smart-default sound effect for a category.
 *
 * Port of selectSmartDefault(for:from:) — uses a seeded rng instead of
 * Int.random so results are deterministic and testable.
 *
 * Strategy (matches Swift original):
 *   1. Filter favorites to those present in `effects` AND not equal to `lastUsed`.
 *   2. If that leaves nothing, fall back to all favorites present in `effects`.
 *   3. If favorites available, pick via rng over top min(5, count) entries.
 *   4. Otherwise, pick from non-lastUsed effects; last resort: any effect.
 */
export function selectSmartDefault(
  category: SoundEffectCategory,
  effects: string[],
  lastUsed: string | null,
  rng: () => number,
): string {
  const favorites = CATEGORY_FAVORITES[category] ?? [];

  // Filter favorites to only available effects AND exclude last used
  let availableFavorites = favorites.filter(f => effects.includes(f) && f !== lastUsed);

  // If we filtered out everything, allow last used but prefer others
  if (availableFavorites.length === 0) {
    availableFavorites = favorites.filter(f => effects.includes(f));
  }

  // If favorites available, pick with weighted randomness over top min(5, count)
  if (availableFavorites.length > 0) {
    const idx = pickIndex(rng, Math.min(5, availableFavorites.length));
    return availableFavorites[idx];
  }

  // Fallback: random from all available effects, excluding last used
  const filteredEffects = effects.filter(e => e !== lastUsed);
  if (filteredEffects.length > 0) {
    return pick(rng, filteredEffects);
  }

  // Last resort: any effect (lastUsed is the only option)
  return effects.length > 0 ? pick(rng, effects) : '';
}

// ---------------------------------------------------------------------------
// selectSemanticEffect — port of selectSemanticEffect(for:) in Swift
// ---------------------------------------------------------------------------

/**
 * Select a specific sound effect using semantic keyword matching.
 *
 * Port of `selectSemanticEffect(for:)`:
 *   1. Lowercase and strip leading/trailing punctuation from each word.
 *   2. Exact whole-word match: return first word whose cleaned form is a map key.
 *   3. Substring match: iterate SEMANTIC_MAP keys in declared insertion order,
 *      return the first key that appears anywhere in the phrase.
 *   4. Return null if no match.
 */
export function selectSemanticEffect(triggerPhrase: string): string | null {
  const phrase = triggerPhrase.toLowerCase();

  // Step 1: exact whole-word match (mirrors Swift trimmingCharacters(in: .punctuationCharacters))
  for (const word of phrase.split(/\s+/)) {
    const cleanWord = word.replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, '');
    if (cleanWord && cleanWord in SEMANTIC_MAP) {
      return SEMANTIC_MAP[cleanWord];
    }
  }

  // Step 2: substring match in declared map insertion order (deterministic)
  for (const key of Object.keys(SEMANTIC_MAP)) {
    if (phrase.includes(key)) {
      return SEMANTIC_MAP[key];
    }
  }

  return null;
}

// ---------------------------------------------------------------------------
// selectEffect — port of selectEffect(for:) in Swift (~lines 76-92)
// ---------------------------------------------------------------------------

/**
 * Select a sound effect for an opportunity.
 *
 * Port of `selectEffect(for:)`:
 *   1. Try semantic matching via selectSemanticEffect.
 *   2. Fall back to selectSmartDefault for the opportunity's category.
 *   Returns null only when the category has no effects at all.
 */
export function selectEffect(
  opportunity: SoundEffectOpportunity,
  lastUsed: string | undefined,
  rng: () => number,
): string | null {
  // First try semantic matching based on trigger phrase
  const semanticEffect = selectSemanticEffect(opportunity.triggerPhrase);
  if (semanticEffect !== null) {
    return semanticEffect;
  }

  // Fallback to category-based selection with smart defaults
  const effects = getEffects(opportunity.suggestedCategory);
  if (effects.length === 0) return null;

  return selectSmartDefault(opportunity.suggestedCategory, effects, lastUsed ?? null, rng);
}
